import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SoftwareCard from "@/components/base/SoftwareCard";
import usePageMeta from "@/hooks/usePageMeta";
import { searchSoftware } from "@/utils/software";
import { softwareList } from "@/mocks/software";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);
  const [platform, setPlatform] = useState("all");
  const [price, setPrice] = useState("all");

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 最近最新手机中文大全10` : "软件搜索 - 最近最新手机中文大全10",
    `在最近最新手机中文大全10中搜索手机中文软件，快速定位所需应用的版本、大小、平台与更新信息。`,
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => {
    let list = searchSoftware(keyword);
    if (platform !== "all") list = list.filter((s) => s.platforms.includes(platform));
    if (price !== "all") list = list.filter((s) => s.price === price);
    return list;
  }, [keyword, platform, price]);

  const recommendations = [...softwareList].sort((a, b) => b.downloads - a.downloads).slice(0, 4);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ q: input.trim() });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-[1240px] mx-auto px-4 md:px-6 pt-6 pb-7">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "软件搜索" }]} />
            <h1 className="mt-4 font-heading text-xl md:text-2xl font-bold text-foreground-950">软件搜索</h1>
            <form onSubmit={submit} className="mt-4 flex items-center gap-2 max-w-2xl">
              <div className="relative flex-1">
                <span className="flex items-center justify-center w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400"><i className="ri-search-line" aria-hidden="true"></i></span>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  type="text"
                  placeholder="输入软件名称、标签或开发者，如：输入法、游戏、出行"
                  className="w-full h-12 pl-10 pr-3 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-400"
                />
              </div>
              <button type="submit" className="h-12 px-6 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                搜索
              </button>
            </form>
          </div>
        </div>

        <div className="w-full max-w-[1240px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              关键词 <span className="text-primary-600 font-medium">“{keyword || "全部"}”</span> 共找到{" "}
              <span className="font-heading font-semibold text-foreground-950">{results.length}</span> 款软件
            </p>
            <div className="flex items-center gap-2">
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="h-9 px-3 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="all">全部平台</option>
                <option value="android">安卓</option>
                <option value="ios">iOS</option>
              </select>
              <select
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="h-9 px-3 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="all">全部类型</option>
                <option value="free">免费</option>
                <option value="paid">付费</option>
              </select>
            </div>
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {results.map((software, i) => (
                <SoftwareCard key={software.id} software={software} index={i} />
              ))}
            </div>
          ) : (
            <div>
              <div className="bg-background-100 rounded-lg border border-background-200 p-10 text-center">
                <span className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-background-200 text-secondary-500 text-3xl">
                  <i className="ri-search-eye-line" aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">未找到相关软件</h3>
                <p className="mt-2 text-sm text-foreground-600">
                  没有找到与“{keyword}”匹配的软件，换个关键词试试，或看看下面的热门推荐。
                </p>
                <Link to="/" className="mt-5 inline-flex items-center gap-2 h-10 px-5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  返回首页浏览分类
                </Link>
              </div>

              <h2 className="mt-10 font-heading text-lg font-bold text-foreground-950 mb-4">热门软件推荐</h2>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {recommendations.map((software, i) => (
                  <SoftwareCard key={software.id} software={software} index={i} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}